import EventEmitter from "../Utils/EventEmitter";
import Experience from "../Experience";
import HUD from "../UI/HUD";

const HIGHSCORE_KEY = "trainRunner.highscore";

function isMobile() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) return true;
  return "ontouchstart" in window && window.innerWidth < 768;
}

export const GameState = {
  MENU: "menu",
  PLAYING: "playing",
  DEAD: "dead",
};

export default class Game extends EventEmitter {
  constructor() {
    super();

    this.experience = new Experience();
    this.input = this.experience.input;
    this.audio = this.experience.audio;
    if (this.audio) this.audio.loadAmbient("/sound/ambience2.mp3");

    this.state = GameState.MENU;
    this.score = 0;
    this.highscore = this._loadHighscore();

    this.selectedLane = 1;
    this.committedLane = 1;
    this._lastCurrentSegment = null;

    this.hud = new HUD();
    this.hud.setScore(this.score);
    this.hud.setHighscore(this.highscore);

    this._wireInput();
    this._enterMenu();
  }

  _wireInput() {
    this.input.on("start", () => {
      if (this.state === GameState.MENU) this.startRun();
    });
    this.input.on("restart", () => {
      if (this.state === GameState.DEAD) this.startRun();
    });
    this.input.on("left", () => this._tryShift(-1));
    this.input.on("right", () => this._tryShift(+1));
  }

  _tryShift(dir) {
    if (this.state !== GameState.PLAYING) return;
    const tm = this.experience.world && this.experience.world.trackManager;
    if (!tm || !tm.inputWindowOpen()) return;
    const next = Math.max(0, Math.min(2, this.selectedLane + dir));
    if (next === this.selectedLane) return;
    this.selectedLane = next;
    this.committedLane = next;
    const train = this.experience.world && this.experience.world.train;
    if (train) train.setLane(next);
    if (this.audio) this.audio.playJump();
  }

  _enterMenu() {
    this.state = GameState.MENU;
    const mobile = isMobile();
    this.hud.showOverlay({
      title: "PORTRA",
      subtitle: (mobile ? "Tap START to begin" : "Press Enter or tap to begin") + "\n",
      instruction: mobile
        ? "Swipe ← → to jump\nTap to shoot"
        : "Move with ← →   \n   Shoot with SPACE",
      buttonLabel: "START",
      onButton: () => this.startRun(),
    });
    this.trigger("stateChange", [this.state]);
  }

  startRun() {
    this.state = GameState.PLAYING;
    this.score = 0;
    this.selectedLane = 1;
    this.committedLane = 1;
    this._lastCurrentSegment = null;
    const world = this.experience.world;
    if (world) {
      if (world.trackManager) world.trackManager.reset();
      if (world.pickups) world.pickups.reset();
      if (world.obstacles) world.obstacles.reset();
      if (world.projectiles) world.projectiles.reset();
      if (world.train) world.train.setLane(1);
    }
    this.hud.setScore(this.score);
    this.hud.hideOverlay();
    if (this.audio) {
      this.audio.playStart();
      this.audio.playAmbient();
    }
    this.trigger("stateChange", [this.state]);
  }

  addScore(delta) {
    if (this.state !== GameState.PLAYING) return;
    this.score += delta;
    this.hud.setScore(this.score);
  }

  die(reason) {
    if (this.state !== GameState.PLAYING) return;
    this.state = GameState.DEAD;
    if (this.score > this.highscore) {
      this.highscore = this.score;
      this._saveHighscore();
      this.hud.setHighscore(this.highscore);
    }
    const isBomb = reason === "Boom";
    const title = isBomb ? "BLOWN TO PIECES" : "DERAILED";
    const flavor = isBomb ? "The bomb won." : reason || "";
    this.hud.showOverlay({
      title,
      subtitle: `Score ${this.score}  ·  Best ${this.highscore}` + (flavor ? `\n${flavor}` : ""),
      buttonLabel: "RUN AGAIN",
      onButton: () => this.startRun(),
    });
    if (this.audio) {
      this.audio.playDeath();
      this.audio.pauseAmbient();
    }
    this.trigger("stateChange", [this.state]);
  }

  isPlaying() {
    return this.state === GameState.PLAYING;
  }

  update() {
    if (this.state !== GameState.PLAYING) return;

    const tm = this.experience.world && this.experience.world.trackManager;
    const train = this.experience.world && this.experience.world.train;
    if (!tm) return;

    const cur = tm.currentSegment();
    if (!cur) return;

    if (this._lastCurrentSegment && cur !== this._lastCurrentSegment) {
      // Segment boundary crossed — verify the player committed to the right lane.
      if (this.selectedLane !== cur.lane) {
        this.die("Missed the rail");
        return;
      }
      if (cur.lane !== this.committedLane) {
        this.committedLane = cur.lane;
        if (train) train.setLane(cur.lane);
      }
    }
    this._lastCurrentSegment = cur;
  }

  _loadHighscore() {
    try {
      const raw = localStorage.getItem(HIGHSCORE_KEY);
      return raw ? Number(raw) || 0 : 0;
    } catch (_e) {
      return 0;
    }
  }

  _saveHighscore() {
    try {
      localStorage.setItem(HIGHSCORE_KEY, String(this.highscore));
    } catch (_e) {
      // ignore (private mode etc.)
    }
  }
}
